import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, makeStyles } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as userSelectors from 'selectors/user';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import FormSection from 'components/templates/FormSection';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import Errors from 'components/shared/Errors';
import DeleteAccount from 'modules/users/containers/delete-account';
import ConfirmPasswordDialog from 'modules/users/components/confirm-password-dialog';
import ConfirmSaveDialog from 'modules/users/components/confirm-save-dialog';
import BankIdLoginDialog from 'modules/users/components/bankid-login-dialog';
import * as actions from 'actions';
import ProfileTabs from 'modules/users/components/ProfileTabs';
import ProfileForm from 'modules/users/components/ProfileForm';
import { UPDATE_SUCCESS } from 'modules/users/constants';
import SCAVerificationDialog from 'components/shared/SCAVerificationDialog';
import { shouldTriggerSCA } from 'logic/sca';
import useValidateNaturalUserEmail from 'hooks/useValidateNaturalUserEmail';

const styles = makeStyles((theme) => ({
  changepwd: {
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
    },
  },
}));

const Profile = () => {
  const classes = styles();
  const [panels, setPanels] = React.useState({
    basic: true,
    password: false,
  });
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(userSelectors.userData);
  const canChangeName = useSelector(userSelectors.canChangeName);
  const updateState = useSelector(userSelectors.updateState);
  const success = useSelector(userSelectors.success);
  const submitErrors = useSelector(userSelectors.errors);
  const isImpersonating = useSelector(userSelectors.isImpersonating);
  const [open, setOpen] = React.useState(false);
  const [openBankid, setOpenBankid] = React.useState(false);
  const [openConfirmSave, setOpenConfirmSave] = React.useState(false);
  const [submittedData, setSubmittedData] = React.useState(null);
  const isNaturalUser = user?.type_of_person === 'NATURAL';
  const isCommissionHost = !!user?.booking_active && !!user?.has_reviewed_cottages;
  const shouldValidateEmail = isNaturalUser && isCommissionHost;
  const emailValidation = useValidateNaturalUserEmail(
    user?.username || user?.email,
    shouldValidateEmail,
  );
  const isEmailBlocked = shouldValidateEmail && (emailValidation.loading || !emailValidation.valid);
  const isEmailValidationLoading = shouldValidateEmail && emailValidation.loading;

  const emailValidationMessage = React.useMemo(() => {
    if (!isEmailBlocked) {
      return null;
    }

    if (emailValidation.disallowedPrefix) {
      return (
        <FormattedMessage
          id="mangopay.email.invalidPrefix"
          defaultMessage="Email addresses starting with {prefix}@ are not allowed for natural hosts.{br}Please update your email in your profile."
          values={{ prefix: emailValidation.disallowedPrefix, br: <br /> }}
        />
      );
    }

    if (emailValidation.error === 'invalid_format') {
      return (
        <FormattedMessage
          id="mangopay.email.invalidFormat"
          defaultMessage="Please enter a valid email address in your profile to continue."
        />
      );
    }

    return (
      <FormattedMessage
        id="mangopay.email.invalidGeneric"
        defaultMessage="This email address cannot be used for payouts. Please update your email in your profile."
      />
    );
  }, [emailValidation.disallowedPrefix, emailValidation.error, isEmailBlocked]);

  const onSubmit = async (values = submittedData) => {
    const triggerSCA = shouldTriggerSCA(values, user);
    dispatch(actions.updateUserRequest({ ...values, language: user.language, triggerSCA }));
  };

  const profileFormInitialValues = React.useMemo(() => {
    const { language, ...rest } = user;
    return rest;
  }, [user]);

  const onPreSubmit = (values) => {
    // Admin acting as the user: no password / BankID needed, just confirm (STU-17).
    if (isImpersonating) {
      setSubmittedData(values);

      return setOpenConfirmSave(true);
    }

    if (user.type === 'STRICT' && !user.bankid_verified_at) {
      setSubmittedData(values);

      return setOpen(true);
    }

    if (user.type === 'STRICT' && user.bankid_verified_at && !user.bankid_session) {
      setSubmittedData(values);

      return setOpenBankid(true);
    }

    return onSubmit(values);
  };

  React.useEffect(() => {
    if (user.type === 'SOFT') {
      setPanels({
        basic: true,
        password: true,
      });
    }
  }, [user.type]);

  const [pendingNext, setPendingNext] = React.useState(null);

  React.useEffect(() => {
    if (updateState !== UPDATE_SUCCESS) return;

    setSubmittedData(null);

    const next = new URLSearchParams(location.search).get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      setPendingNext(next);
    }

    dispatch(actions.resetUserForm());
  }, [updateState, location.search, dispatch]);

  React.useEffect(() => {
    if (pendingNext) {
      history.push(pendingNext);
      setPendingNext(null);
    }
  }, [pendingNext, history]);

  return (
    <Main title={<FormattedMessage id="userform.myProfile" defaultMessage="My profile" />}>
      <ProfileTabs
        tab={0}
        bookingActive={!!(user.has_reviewed_cottages || user.dac7_valid)}
        payoutDisabled={isEmailBlocked}
      />
      {isEmailBlocked && !isEmailValidationLoading && (
        <Box mt={2} display="flex" justifyContent="center">
          <Box width="100%" maxWidth={660}>
            <Alert severity="warning" elevation={3}>
              {emailValidationMessage}
            </Alert>
          </Box>
        </Box>
      )}
      <BankIdLoginDialog
        open={openBankid}
        header={<FormattedMessage id="bankid.login" defaultMessage="Login with BankID" />}
        title={
          <Box textAlign="center">
            <FormattedMessage
              id="bankid.security"
              defaultMessage="For security reasons, please log in with your BankID before continuing"
            />
          </Box>
        }
        onSuccess={() => {
          setOpenBankid(false);
          onSubmit(submittedData);
        }}
        onClose={() => setOpenBankid(false)}
      />
      <ConfirmPasswordDialog
        onClose={() => setOpen(false)}
        open={open}
        onSuccess={(password) => {
          setOpen(false);

          return onSubmit({
            ...submittedData,
            password,
          });
        }}
      />

      <ConfirmSaveDialog
        open={openConfirmSave}
        onClose={() => setOpenConfirmSave(false)}
        onConfirm={() => {
          setOpenConfirmSave(false);
          onSubmit(submittedData);
        }}
      />

      <SCAVerificationDialog />

      <FormSection clear>
        <ProfileForm
          canChangeName={canChangeName}
          onSubmit={onPreSubmit}
          panels={panels}
          initialValues={profileFormInitialValues}
        />
        <hr />
        <Box py={2} textAlign="right">
          {user.type === 'STRICT' && (
            <Typography variant="body2" component="span" className={classes.changepwd}>
              <Link to="/change-password">
                <FormattedMessage id="userform.changePassword" defaultMessage="Change password" />
              </Link>
            </Typography>
          )}
          &nbsp;&nbsp;&nbsp;
          <DeleteAccount />
        </Box>
      </FormSection>

      <Errors errors={submitErrors} />
      <Success success={success} />
    </Main>
  );
};

export default Profile;
